import type { QueryClient } from '@tanstack/react-query';
import { data, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import { isApiError } from '../api';
import { askRunQuery, askStartersQuery } from '../api/ai';
import { overviewQuery } from '../api/insights';
import { noAccess } from '../app/access';
import type { RouteHandle } from '../app/RootLayout';
import { forRoles } from '../search/pages';
import { requireMe } from '../session/loaders';

const handle: RouteHandle = {
  help: { pageLabel: 'Insights · Ask the data', topicIds: [], pageKey: 'insights-ask' },
  search: {
    label: 'Ask the data',
    meta: 'Insights · ask about awards, recognition and budgets in plain words',
    // APP-191 D15 A and D16: the Stock Admin team and Sector Leads (their own sectors) ask too.
    // System Admins see every page (APP-134).
    visible: forRoles('executive', 'stock_admin', 'sector_lead'),
  },
};

/** Who the page is for, as No access names them. System Admins see every page. */
const FOR = 'Executives, the Stock Admin team and Sector Leads';

export interface AskDataLoaderData {
  /** Preview shows sample data, so Ask the data doesn't run there. */
  preview: boolean;
}

/**
 * Insights › Ask the data (RPT5): its own route entry, beside the rest of Insights. For Executives,
 * the Stock Admin team (APP-191 D15 A), Sector Leads for their own sectors (D16) and System Admins
 * (APP-134); anyone else is told whose page it is. The code loads on first use.
 *
 * APP-191 slice 3 (D8, D9, D11): one page for a new conversation, a kept one
 * (`insights/ask/c/:conversationId`) and a project's board (`insights/ask/p/:projectId`). They're
 * children of the one page, so moving between them keeps the page, and a new conversation that
 * gets its address keeps what's on screen.
 *
 * SENT-171: the children have no loaders. React Router runs a child's loader beside its parent's,
 * before the parent has decided access, and reading a conversation or a board counts in the
 * person's re-run allowance on the server even when it's refused. So the page reads them itself,
 * once it's open: never in a sample Preview, never for someone the page isn't for, and never for
 * an address that can't be one (ids are UUIDs, `isKeptId`).
 */
export function askDataRoutes(queryClient: QueryClient): RouteObject[] {
  return [
    {
      path: 'insights/ask',
      loader: async ({ request }: LoaderFunctionArgs): Promise<AskDataLoaderData> => {
        await requireMe(queryClient, request);
        try {
          // The Insights tabs come from the overview's library, as on every Insights page.
          await queryClient.query(overviewQuery());
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router's 403 response
          if (isApiError(error) && error.status === 403) throw noAccess(FOR, error);
          // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router's 404 response
          if (isApiError(error) && error.status === 404) throw data(null, { status: 404 });
          throw error;
        }
        try {
          const starters = await queryClient.query(askStartersQuery);
          // The hero's coverage figure: the coverage starter, run without AI.
          const coverage = starters.starters.find((s) => s.query.analysis === 'coverage');
          if (coverage !== undefined)
            await queryClient.query(askRunQuery(coverage.query)).catch(() => null);
          return { preview: false };
        } catch (error) {
          if (isApiError(error) && error.code === 'ai_unavailable') return { preview: true };
          // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router's 403 response
          if (isApiError(error) && error.status === 403) throw noAccess(FOR, error);
          throw error;
        }
      },
      lazy: async () => ({ Component: (await import('./AskDataPage')).AskDataPage }),
      handle,
      children: [{ index: true }, { path: 'c/:conversationId' }, { path: 'p/:projectId' }],
    },
  ];
}
